import { Box, Flex } from '@chakra-ui/layout';
import { FormWrapper } from 'library/molecules/Form/FormWrapper/FormWrapper';
import { CustomDrawerComponent } from 'library/molecules';
import FilterForm from './Form';
import FilterFormFooter from './Footer';
import { IncidentAnalyticsFilterForm } from 'views/main/organization/analytics-v3/hooks/types';
import { useGetAnalyticsFilters } from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import { useAnalyticsV3Context } from 'views/main/organization/analytics-v3/hooks/useAnalyticsV3Context';
import { getServicesList, getTagMap } from 'views/main/organization/analytics-v3/helpers';
import { useShallow } from 'zustand/react/shallow';
import { ANALYTIC_V3_EVENTS } from 'views/main/organization/analytics-v3/constants';
import { AppTracker } from 'shared/analytics/tracker';

const FilterDrawer = ({ isOrgAnalytics = false }: { isOrgAnalytics?: boolean }) => {
  const { data, isLoading } = useGetAnalyticsFilters();
  const isFilterDrawerOpen = useAnalyticsV3Context(state => state.isFilterDrawerOpen);
  const setIsFilterDrawerOpen = useAnalyticsV3Context(state => state.setIsFilterDrawerOpen);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const setFilters = useAnalyticsV3Context(useShallow(state => state.setFilters));
  const tabIndex = useAnalyticsV3Context(useShallow(state => state.tabIndex));

  if (isLoading || !data) return null;

  const tagMap = getTagMap(data?.tags || []);

  const services = getServicesList(data?.service || []);

  const serviceOwnersMap = data?.serviceOwnerMap || {};

  const onSubmit = (filters: IncidentAnalyticsFilterForm) => {
    const tabNameMap = {
      1: 'Incident Analytics',
      2: 'Response Metrics',
      3: 'Noise Reduction',
    };
    const filterNamesMap = {
      service: 'Service',
      serviceOwner: 'Service Owner',
      assignee: 'Assignee',
      priority: 'Priority',
      tagValue: 'Tag',
    };
    const filterFieldsUsed = Object.keys(filters)
      .filter(key => {
        const value = filters[key as keyof IncidentAnalyticsFilterForm];
        if (value === undefined) return false;
        if (value === null) return false;
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'object') {
          return Object.values(value).some(v => v.length > 0);
        }

        return true;
      })
      .map(key => {
        return filterNamesMap[key as keyof typeof filterNamesMap];
      })
      .filter(Boolean);

    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_FILTER_APPLIED,
      {
        'Analytics Scope': 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': tabNameMap[tabIndex as keyof typeof tabNameMap],
        'Filter Fields Used': filterFieldsUsed,
      },
      true,
    );
    setFilters(filters);
    setIsFilterDrawerOpen(false);
  };

  return (
    <CustomDrawerComponent
      title="Filter"
      disableBodyPadding
      size="sm"
      isOpen={isFilterDrawerOpen}
      onClose={() => setIsFilterDrawerOpen(false)}
    >
      <FormWrapper<IncidentAnalyticsFilterForm> onSubmit={onSubmit} defaultValues={filters || {}}>
        <Flex direction="column" justifyContent="space-between" height="100%">
          <Box sx={{ '& > form': { mb: 20 } }}>
            <FilterForm
              tagMap={tagMap}
              services={services}
              serviceOwnersMap={serviceOwnersMap}
              meAndMySquads={data.assignee.youAndYourSquads}
            />
          </Box>
          <FilterFormFooter />
        </Flex>
      </FormWrapper>
    </CustomDrawerComponent>
  );
};

export default FilterDrawer;
